import {authDropdownIcons} from '@app/auth/auth-dropdown-icons';
import {useLogout} from '@common/auth/requests/logout';
import {useAuth} from '@common/auth/use-auth';
import {SiteConfigContext} from '@common/core/settings/site-config-context';
import {ThemeSelectorDialog} from '@common/ui/theme-selector/theme-selector-dialog';
import {useCustomMenu} from '@common/menus/use-custom-menu';
import {useNavigate} from '@common/ui/navigation/use-navigate';
import {Placement} from '@floating-ui/react-dom';
import {ListboxItemProps} from '@ui/forms/listbox/item';
import {Trans} from '@ui/i18n/trans';
import {createSvgIconFromTree} from '@ui/icons/create-svg-icon';
import {LucideIcon} from '@ui/icons/lucide/lucide-icon-wrapper';
import {SvgIconProps} from '@ui/icons/svg-icon';
import {Menu, MenuItem, MenuTrigger} from '@ui/menu/menu-trigger';
import {openDialog} from '@ui/overlays/store/dialog-store';
import {useSettings} from '@ui/settings/use-settings';
import {
  BellIcon,
  CircleUser,
  CreditCardIcon,
  LogOutIcon,
  PaletteIcon,
} from 'lucide-react';
import {cloneElement, ReactElement, useContext} from 'react';

interface Props {
  children: ReactElement;
  items?: ReactElement<ListboxItemProps>[];
  placement?: Placement;
  accountSettingsLink?: string;
}
export function NavbarAuthMenu({
  children,
  items,
  placement,
  accountSettingsLink,
}: Props) {
  const {auth} = useContext(SiteConfigContext);
  const logout = useLogout();
  const menu = useCustomMenu('auth-dropdown');
  const {notifications, themes} = useSettings();
  const {user, isSubscribed} = useAuth();
  const navigate = useNavigate();
  if (!user) return null;
  const hasUnreadNotif = !!user.unread_notifications_count;

  const notifMenuItem = (
    <MenuItem
      className="md:hidden"
      value="notifications"
      startIcon={<LucideIcon icon={BellIcon} size="xs" />}
      onSelected={() => {
        navigate('/notifications');
      }}
    >
      <Trans message="Notifications" />
      {hasUnreadNotif ? ` (${user.unread_notifications_count})` : undefined}
    </MenuItem>
  );

  const billingMenuItem = (
    <MenuItem
      value="billing"
      startIcon={<LucideIcon icon={CreditCardIcon} size="xs" />}
      onSelected={() => {
        navigate('/billing');
      }}
    >
      <Trans message="Billing" />
    </MenuItem>
  );

  return (
    <MenuTrigger placement={placement}>
      {children}
      <Menu>
        {menu &&
          menu.items.map(item => {
            let icon: ReactElement<SvgIconProps> | null = null;

            if (item.icon) {
              const IconCmp = createSvgIconFromTree(item.icon);
              icon = IconCmp && <IconCmp size="xs" />;
            } else {
              const IconCmp = authDropdownIcons[item.action.split('?')[0]];
              icon =
                IconCmp &&
                cloneElement(IconCmp, {size: IconCmp.props.size ?? 'xs'});
            }

            let action = item.action;

            if (action === '/account-settings' && accountSettingsLink) {
              action = accountSettingsLink;
            }

            return (
              <MenuItem
                value={item.id}
                key={item.id}
                startIcon={icon}
                onSelected={() => {
                  const appPath = toAppPath(action);
                  if (
                    item.type === 'link' &&
                    !isSameOriginAppUrl(action, item.target)
                  ) {
                    window.open(action, '_blank');
                  } else {
                    navigate(appPath);
                  }
                }}
              >
                <Trans message={item.label} />
              </MenuItem>
            );
          })}
        {auth?.getUserProfileLink && (
          <MenuItem
            value="profile"
            startIcon={<LucideIcon icon={CircleUser} size="xs" />}
            onSelected={() => {
              navigate(encodeURI(auth.getUserProfileLink!(user)));
            }}
          >
            <Trans message="Profile page" />
          </MenuItem>
        )}
        {items?.map(item => item)}
        {notifications?.integrated ? notifMenuItem : undefined}
        {isSubscribed && billingMenuItem}
        {themes?.user_change && (
          <MenuItem
            value="choose-theme"
            startIcon={<LucideIcon icon={PaletteIcon} size="xs" />}
            onSelected={() => {
              openDialog(ThemeSelectorDialog);
            }}
          >
            <Trans message="Themes" />
          </MenuItem>
        )}

        <MenuItem
          value="logout"
          startIcon={<LucideIcon icon={LogOutIcon} size="xs" />}
          onSelected={() => {
            logout.mutate();
          }}
        >
          <Trans message="Log out" />
        </MenuItem>
      </Menu>
    </MenuTrigger>
  );
}

function isSameOriginAppUrl(action: string, target?: string): boolean {
  if (target === '_blank') {
    return false;
  }

  try {
    const url = new URL(action, window.location.origin);
    return url.origin === window.location.origin;
  } catch {
    return action.startsWith('/');
  }
}

function toAppPath(action: string): string {
  try {
    const url = new URL(action, window.location.origin);
    return url.pathname + url.search + url.hash;
  } catch {
    return action;
  }
}
